<script setup>
import { useTemplateRef, onBeforeUnmount, computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useTrack } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import { vOnClickOutside } from '@vueuse/components';
import { CONVERSATION_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import { useConversationFilterContext } from './provider.js';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { FILTER_ICON_CHOICES } from 'dashboard/components/widgets/conversation/conversationQuickFilters';

import Button from 'next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import ConditionRow from './ConditionRow.vue';

const props = defineProps({
  isFolderView: {
    type: Boolean,
    default: false,
  },
  folderName: {
    type: String,
    default: '',
  },
  contained: {
    type: Boolean,
    default: false,
  },
  // SiteUp: create from Atalhos (+) — Save shortcut + Cancel only + icon picker.
  shortcutMode: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'applyFilter',
  'saveFilter',
  'updateFolder',
  'close',
]);
const { filterTypes } = useConversationFilterContext();

const filters = defineModel({
  type: Array,
  default: [],
});
const folderNameLocal = ref(props.folderName);
const selectedIcon = ref(FILTER_ICON_CHOICES[0] || 'i-ri-folder-line');

const DEFAULT_FILTER = {
  attributeKey: 'status',
  filterOperator: 'equal_to',
  values: [],
  queryOperator: 'and',
};

const { t } = useI18n();
const store = useStore();

const resetFilter = () => {
  filters.value = [{ ...DEFAULT_FILTER }];
};

const removeFilter = index => {
  if (filters.value.length === 1) {
    resetFilter();
  } else {
    filters.value.splice(index, 1);
  }
};

const addFilter = () => {
  filters.value.push({ ...DEFAULT_FILTER });
};

const conditionsRef = useTemplateRef('conditionsRef');

const isConditionsValid = () => {
  return conditionsRef.value.every(condition => condition.validate());
};

const updateSavedCustomViews = () => {
  if (isConditionsValid()) {
    emit('updateFolder', filters.value, folderNameLocal.value);
  }
};

const saveAndOpen = () => {
  if (!folderNameLocal.value || !isConditionsValid()) {
    return;
  }

  emit('saveFilter', filters.value, folderNameLocal.value, selectedIcon.value);
};

function validateAndSubmit() {
  if (!isConditionsValid()) {
    return;
  }

  store.dispatch(
    'setConversationFilters',
    useSnakeCase(JSON.parse(JSON.stringify(filters.value)))
  );
  emit('applyFilter', filters.value);
  useTrack(CONVERSATION_EVENTS.APPLY_FILTER, {
    appliedFilters: filters.value.map(filter => ({
      key: filter.attributeKey,
      operator: filter.filterOperator,
      queryOperator: filter.queryOperator,
    })),
  });
}

const filterModalHeaderTitle = computed(() => {
  if (props.shortcutMode) {
    return t('FILTER.CUSTOM_VIEWS.ADD.TITLE');
  }
  return !props.isFolderView
    ? t('FILTER.TITLE')
    : t('FILTER.EDIT_CUSTOM_FILTER');
});

const iconChoices = FILTER_ICON_CHOICES;

onBeforeUnmount(() => emit('close'));
const outsideClickHandler = [
  () => emit('close'),
  {
    ignore: [
      '#toggleConversationFilterButton',
      '[data-testid="quick-filter-create-custom-atalhos"]',
      '[data-testid="quick-filter-more"]',
    ],
  },
];
</script>

<template>
  <div
    v-on-click-outside="outsideClickHandler"
    data-testid="conversation-filter-drawer"
    class="z-40 w-full border border-n-weak"
    :class="
      props.contained
        ? 'flex h-full min-h-0 max-h-full max-w-full flex-col overflow-hidden rounded-2xl bg-n-solid-1'
        : 'grid max-w-3xl content-start gap-6 overflow-visible rounded-2xl bg-n-alpha-3 p-6 shadow-lg backdrop-blur-[100px] lg:w-[750px]'
    "
  >
    <div
      class="flex items-center justify-between gap-2"
      :class="
        props.contained ? 'shrink-0 border-b border-n-weak px-3 py-2.5' : ''
      "
    >
      <h3 class="text-base font-medium leading-6 text-n-slate-12">
        {{ filterModalHeaderTitle }}
      </h3>
      <Button
        v-if="props.contained"
        xs
        ghost
        slate
        icon="i-ri-close-line"
        :aria-label="t('FILTER.CANCEL_BUTTON_LABEL')"
        data-testid="conversation-filter-close"
        @click="emit('close')"
      />
    </div>

    <div
      :class="
        props.contained
          ? 'flex min-h-0 flex-1 flex-col gap-4 overflow-x-hidden overflow-y-auto px-3 py-3'
          : 'contents'
      "
    >
      <div
        class="border-b border-n-weak"
        :class="props.contained ? 'pb-3' : 'pb-6'"
      >
        <div class="flex items-start gap-3">
          <div
            v-if="shortcutMode || !isFolderView"
            class="shrink-0"
            data-testid="filter-icon-picker"
          >
            <p class="mb-1.5 text-sm font-medium text-n-slate-12">
              {{ t('FILTER.CUSTOM_VIEWS.ADD.ICON_LABEL') }}
            </p>
            <div
              class="grid max-h-28 w-[7.5rem] grid-cols-4 gap-1 overflow-y-auto rounded-xl border border-n-weak bg-n-slate-2 p-1.5"
            >
              <button
                v-for="icon in iconChoices"
                :key="icon"
                type="button"
                class="inline-flex size-7 items-center justify-center rounded-md transition-colors"
                :class="
                  selectedIcon === icon
                    ? 'bg-n-brand text-white'
                    : 'text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12'
                "
                :aria-label="icon"
                :aria-pressed="selectedIcon === icon"
                :data-testid="`filter-icon-${icon}`"
                @click="selectedIcon = icon"
              >
                <Icon :icon="icon" class="size-3.5" />
              </button>
            </div>
          </div>
          <div class="min-w-0 flex-1">
            <Input
              v-model="folderNameLocal"
              :label="
                props.isFolderView
                  ? t('FILTER.FOLDER_LABEL')
                  : t('FILTER.CUSTOM_VIEWS.ADD.LABEL')
              "
              :placeholder="
                props.isFolderView
                  ? t('FILTER.INPUT_PLACEHOLDER')
                  : t('FILTER.CUSTOM_VIEWS.ADD.PLACEHOLDER')
              "
            />
          </div>
        </div>
      </div>
      <ul class="reset-base grid list-none gap-4">
        <template v-for="(filter, index) in filters" :key="filter.id">
          <ConditionRow
            v-if="index === 0"
            ref="conditionsRef"
            :key="`filter-${filter.attributeKey}-0`"
            v-model:attribute-key="filter.attributeKey"
            v-model:filter-operator="filter.filterOperator"
            v-model:values="filter.values"
            :filter-types="filterTypes"
            :show-query-operator="false"
            :contained="props.contained"
            @remove="removeFilter(index)"
          />
          <ConditionRow
            v-else
            :key="`filter-${filter.attributeKey}-${index}`"
            ref="conditionsRef"
            v-model:attribute-key="filter.attributeKey"
            v-model:filter-operator="filter.filterOperator"
            v-model:query-operator="filters[index - 1].queryOperator"
            v-model:values="filter.values"
            show-query-operator
            :filter-types="filterTypes"
            :contained="props.contained"
            @remove="removeFilter(index)"
          />
        </template>
      </ul>
      <Button
        v-if="props.contained"
        sm
        ghost
        blue
        class="w-full justify-center"
        @click="addFilter"
      >
        {{ $t('FILTER.ADD_NEW_FILTER') }}
      </Button>
    </div>

    <div
      class="flex gap-3 border-t border-n-weak"
      :class="
        props.contained
          ? 'shrink-0 flex-col px-3 py-3'
          : 'flex-wrap justify-between pt-3'
      "
    >
      <Button
        v-if="!props.contained && !shortcutMode"
        sm
        ghost
        blue
        @click="addFilter"
      >
        {{ $t('FILTER.ADD_NEW_FILTER') }}
      </Button>
      <div
        class="gap-2"
        :class="props.contained ? 'grid w-full' : 'flex flex-wrap'"
      >
        <template v-if="shortcutMode">
          <Button
            sm
            solid
            blue
            :disabled="!folderNameLocal"
            :class="{ 'w-full justify-center': props.contained }"
            data-testid="filter-save-shortcut"
            @click="saveAndOpen"
          >
            {{ t('FILTER.CUSTOM_VIEWS.ADD.SAVE_BUTTON') }}
          </Button>
          <Button
            sm
            faded
            slate
            :class="{ 'w-full justify-center': props.contained }"
            data-testid="filter-cancel-shortcut"
            @click="emit('close')"
          >
            {{ t('FILTER.CUSTOM_VIEWS.ADD.CANCEL_BUTTON') }}
          </Button>
        </template>
        <template v-else>
          <Button
            sm
            faded
            slate
            :class="{ 'w-full justify-center': props.contained }"
            @click="resetFilter"
          >
            {{ t('FILTER.CLEAR_BUTTON_LABEL') }}
          </Button>
          <Button
            v-if="isFolderView"
            sm
            solid
            blue
            :disabled="!folderNameLocal"
            :class="{ 'w-full justify-center': props.contained }"
            @click="updateSavedCustomViews"
          >
            {{ t('FILTER.UPDATE_BUTTON_LABEL') }}
          </Button>
          <template v-else>
            <Button
              sm
              faded
              slate
              :class="{ 'w-full justify-center': props.contained }"
              @click="validateAndSubmit"
            >
              {{ t('FILTER.SUBMIT_BUTTON_LABEL') }}
            </Button>
            <Button
              sm
              solid
              blue
              :disabled="!folderNameLocal"
              :class="{ 'w-full justify-center': props.contained }"
              @click="saveAndOpen"
            >
              {{ t('FILTER.CUSTOM_VIEWS.ADD.SAVE_BUTTON') }}
            </Button>
          </template>
        </template>
      </div>
    </div>
  </div>
</template>
